/**
 * AdministrationsSection
 * Lists recorded vaccine administrations for a traveler with their CAIR
 * submission status, and lets the pharmacist resubmit rejected/failed/blocked
 * submissions or fix the patient demographics CAIR requires.
 */

import { useCallback, useEffect, useState, type CSSProperties } from "react";
import {
  cairService,
  notifyAdministrationsUpdated,
  ADMINISTRATIONS_UPDATED_EVENT,
  CairReferenceCodes,
  CairStatus,
  VaccineAdministration,
} from "@/services/cair";
import { CairDemographicsForm } from "./CairDemographicsForm";
import { formatDateTime } from "@/utils/formatters";

interface AdministrationsSectionProps {
  travelerId: string;
}

interface StatusChipConfig {
  label: string;
  background: string;
  color: string;
  border: string;
}

const STATUS_CHIPS: Record<CairStatus, StatusChipConfig> = {
  queued: {
    label: "Queued",
    background: "#fffbeb",
    color: "#92400e",
    border: "#fcd34d",
  },
  submitted: {
    label: "Submitted",
    background: "#fffbeb",
    color: "#92400e",
    border: "#fcd34d",
  },
  accepted: {
    label: "Accepted",
    background: "#f0fdf4",
    color: "#166534",
    border: "#86efac",
  },
  accepted_with_errors: {
    label: "Accepted w/ warnings",
    background: "#fffbeb",
    color: "#92400e",
    border: "#fcd34d",
  },
  rejected: {
    label: "Rejected",
    background: "#fef2f2",
    color: "#991b1b",
    border: "#fecaca",
  },
  blocked_missing_data: {
    label: "Blocked — missing patient info",
    background: "#f3f4f6",
    color: "#4b5563",
    border: "#d1d5db",
  },
  failed: {
    label: "Failed",
    background: "#fef2f2",
    color: "#991b1b",
    border: "#fecaca",
  },
};

const FALLBACK_CHIP: StatusChipConfig = {
  label: "Unknown",
  background: "#f3f4f6",
  color: "#4b5563",
  border: "#d1d5db",
};

const cellStyle: CSSProperties = {
  padding: "10px 12px",
  fontSize: "13px",
  color: "#1f2937",
  borderBottom: "1px solid #e5e7eb",
  verticalAlign: "top",
};

const headerCellStyle: CSSProperties = {
  ...cellStyle,
  fontSize: "11px",
  fontWeight: 700,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  borderBottom: "2px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const RESUBMITTABLE_STATUSES: CairStatus[] = [
  "rejected",
  "failed",
  "blocked_missing_data",
];

export function AdministrationsSection({
  travelerId,
}: AdministrationsSectionProps) {
  const [administrations, setAdministrations] = useState<
    VaccineAdministration[]
  >([]);
  const [refCodes, setRefCodes] = useState<CairReferenceCodes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>(
    {}
  );
  const [resubmittingIds, setResubmittingIds] = useState<
    Record<string, boolean>
  >({});
  const [rowMessages, setRowMessages] = useState<Record<string, string>>({});
  const [showDemographicsForm, setShowDemographicsForm] = useState(false);
  const [demographicsMissingFields, setDemographicsMissingFields] = useState<
    string[]
  >([]);

  const fetchAdministrations = useCallback(async () => {
    if (!travelerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cairService.listAdministrations(travelerId);
      setAdministrations(data);
    } catch (err) {
      console.error("Failed to load administrations:", err);
      setError("Failed to load recorded administrations.");
    } finally {
      setLoading(false);
    }
  }, [travelerId]);

  useEffect(() => {
    fetchAdministrations();
    cairService
      .getReferenceCodes()
      .then(setRefCodes)
      .catch(() => {
        // Vaccine names fall back to the protocol key without reference codes
      });
  }, [fetchAdministrations]);

  // Refetch when an administration is recorded elsewhere on the page
  useEffect(() => {
    const handleUpdated = () => fetchAdministrations();
    window.addEventListener(ADMINISTRATIONS_UPDATED_EVENT, handleUpdated);
    return () =>
      window.removeEventListener(ADMINISTRATIONS_UPDATED_EVENT, handleUpdated);
  }, [fetchAdministrations]);

  const getVaccineName = (admin: VaccineAdministration): string => {
    const mapping = refCodes?.cvx_mappings.find(
      (m) => m.cvx_code === admin.cvx_code
    );
    return mapping?.cvx_description || admin.protocol_key;
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleResubmit = async (admin: VaccineAdministration) => {
    setResubmittingIds((prev) => ({ ...prev, [admin.id]: true }));
    setRowMessages((prev) => ({ ...prev, [admin.id]: "" }));
    try {
      const response = await cairService.resubmitCairSubmission(admin.id);
      if (response.cair_status === "blocked_missing_data") {
        const missing = response.missing_demographics || [];
        setRowMessages((prev) => ({
          ...prev,
          [admin.id]: `Still blocked — missing: ${missing.join(", ")}`,
        }));
        setDemographicsMissingFields(missing);
        setShowDemographicsForm(true);
      } else {
        notifyAdministrationsUpdated();
      }
      await fetchAdministrations();
    } catch (err) {
      console.error("Failed to resubmit to CAIR:", err);
      setRowMessages((prev) => ({
        ...prev,
        [admin.id]: "Failed to resubmit. Please try again.",
      }));
    } finally {
      setResubmittingIds((prev) => ({ ...prev, [admin.id]: false }));
    }
  };

  const renderDetails = (admin: VaccineAdministration) => {
    const details: Array<{ label: string; value: string | null }> = [
      { label: "CAIR ack code", value: admin.cair_ack_code },
      { label: "CAIR details", value: admin.cair_ack_details },
      { label: "Error", value: admin.cair_error_message },
      { label: "Submission ID", value: admin.cair_submission_id },
      {
        label: "Submitted at",
        value: admin.cair_submitted_at
          ? formatDateTime(admin.cair_submitted_at)
          : null,
      },
    ].filter((d) => d.value);

    if (details.length === 0) {
      return (
        <div style={{ fontSize: "13px", color: "#6b7280" }}>
          No additional details from CAIR.
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {details.map((d) => (
          <div key={d.label} style={{ fontSize: "13px", color: "#374151" }}>
            <span style={{ fontWeight: 600 }}>{d.label}: </span>
            <span style={{ whiteSpace: "pre-wrap" }}>{d.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="info-card administrations-section">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <h3 style={{ marginBottom: 0 }}>Recorded Administrations (CAIR)</h3>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => {
              setDemographicsMissingFields([]);
              setShowDemographicsForm(true);
            }}
            style={{
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#374151",
              backgroundColor: "white",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Edit Patient Info
          </button>
          <button
            onClick={fetchAdministrations}
            disabled={loading}
            style={{
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: 600,
              color: loading ? "#9ca3af" : "#0f766e",
              backgroundColor: loading ? "#f9fafb" : "#f0fdfa",
              border: `1px solid ${loading ? "#e5e7eb" : "#14b8a6"}`,
              borderRadius: "6px",
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div style={{ marginTop: "16px" }}>
        {error ? (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "13px",
              color: "#991b1b",
            }}
          >
            {error}{" "}
            <button
              onClick={fetchAdministrations}
              style={{
                background: "none",
                border: "none",
                color: "#991b1b",
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: "13px",
                padding: 0,
              }}
            >
              Retry
            </button>
          </div>
        ) : loading && administrations.length === 0 ? (
          <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>
            Loading administrations...
          </p>
        ) : administrations.length === 0 ? (
          <p
            style={{
              color: "#6b7280",
              fontSize: "14px",
              fontStyle: "italic",
              margin: 0,
            }}
          >
            No vaccine administrations recorded yet.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...headerCellStyle, textAlign: "left" }}>
                    Vaccine
                  </th>
                  <th style={{ ...headerCellStyle, textAlign: "left" }}>
                    Administered
                  </th>
                  <th style={{ ...headerCellStyle, textAlign: "left" }}>
                    Lot
                  </th>
                  <th style={{ ...headerCellStyle, textAlign: "left" }}>
                    CAIR Status
                  </th>
                  <th style={{ ...headerCellStyle, textAlign: "left" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {administrations.map((admin) => {
                  const chip =
                    STATUS_CHIPS[admin.cair_status] || FALLBACK_CHIP;
                  const hasDetails = Boolean(
                    admin.cair_ack_details ||
                      admin.cair_error_message ||
                      admin.cair_ack_code
                  );
                  const canResubmit = RESUBMITTABLE_STATUSES.includes(
                    admin.cair_status
                  );
                  const isExpanded = expandedRows[admin.id];

                  return (
                    <tr key={admin.id}>
                      <td style={cellStyle}>
                        <div style={{ fontWeight: 600 }}>
                          {getVaccineName(admin)}
                        </div>
                        <div style={{ fontSize: "11px", color: "#6b7280" }}>
                          CVX {admin.cvx_code}
                          {admin.dose_number
                            ? ` · Dose ${admin.dose_number}`
                            : ""}
                        </div>
                      </td>
                      <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                        {formatDateTime(admin.administered_at)}
                      </td>
                      <td style={cellStyle}>{admin.lot_number}</td>
                      <td style={cellStyle}>
                        <span
                          title={
                            admin.cair_status === "accepted_with_errors"
                              ? admin.cair_ack_details || undefined
                              : admin.cair_status === "failed"
                                ? admin.cair_error_message || undefined
                                : undefined
                          }
                          style={{
                            display: "inline-block",
                            padding: "2px 10px",
                            fontSize: "11px",
                            fontWeight: 700,
                            borderRadius: "9999px",
                            backgroundColor: chip.background,
                            color: chip.color,
                            border: `1px solid ${chip.border}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chip.label}
                        </span>
                        {admin.cair_status === "failed" &&
                          admin.cair_error_message && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#991b1b",
                                marginTop: "4px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {admin.cair_error_message}
                            </div>
                          )}
                        {hasDetails && (
                          <div>
                            <button
                              onClick={() => toggleRow(admin.id)}
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                marginTop: "4px",
                                fontSize: "12px",
                                color: "#0f766e",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              {isExpanded ? "Hide details" : "Show details"}
                            </button>
                            {isExpanded && (
                              <div
                                style={{
                                  marginTop: "6px",
                                  padding: "8px 10px",
                                  backgroundColor: "#f9fafb",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "8px",
                                }}
                              >
                                {renderDetails(admin)}
                              </div>
                            )}
                          </div>
                        )}
                        {rowMessages[admin.id] && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#92400e",
                              marginTop: "4px",
                            }}
                          >
                            {rowMessages[admin.id]}
                          </div>
                        )}
                      </td>
                      <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                        {canResubmit && (
                          <button
                            onClick={() => handleResubmit(admin)}
                            disabled={resubmittingIds[admin.id]}
                            style={{
                              padding: "5px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: resubmittingIds[admin.id]
                                ? "#9ca3af"
                                : "#ffffff",
                              backgroundColor: resubmittingIds[admin.id]
                                ? "#e5e7eb"
                                : "#14b8a6",
                              border: "none",
                              borderRadius: "6px",
                              cursor: resubmittingIds[admin.id]
                                ? "not-allowed"
                                : "pointer",
                            }}
                          >
                            {resubmittingIds[admin.id]
                              ? "Resubmitting..."
                              : "Resubmit to CAIR"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CairDemographicsForm
        isOpen={showDemographicsForm}
        travelerId={travelerId}
        missingFields={demographicsMissingFields}
        onClose={() => setShowDemographicsForm(false)}
        onSaved={fetchAdministrations}
      />
    </div>
  );
}

export default AdministrationsSection;
